import { collection, doc, getDoc, getDocs, onSnapshot, runTransaction } from "firebase/firestore";
import { BranchAlreadyExistsError, BranchNotFoundError, ConcurrencyConflictError, DirtyHeadError, GarbageCollectionPlanStaleError, hashState, PersistenceError, RepositoryAlreadyExistsError, RepositoryNotFoundError, RevisionNotFoundError, stableStringify, TagAlreadyExistsError, TagNotFoundError, TagRevisionMismatchError } from "@bjalon/object-vcs-core";
export function firebasePersistence(options) {
    const resolvedOptions = resolveOptions(options);
    const refs = createReferenceFactory(resolvedOptions);
    return {
        async getRepo(input) {
            const repoSnapshot = await getDoc(refs.repo(input.repoId));
            if (!repoSnapshot.exists()) {
                return null;
            }
            return repoRecordFromDocument(input.repoId, readDocumentData(repoSnapshot));
        },
        async createRepo(input) {
            return runTransaction(resolvedOptions.db, async (transaction) => {
                const repoReference = refs.repo(input.repoId);
                const repoSnapshot = await transaction.get(repoReference);
                if (repoSnapshot.exists()) {
                    throw new RepositoryAlreadyExistsError(`Repository "${input.repoId}" already exists.`);
                }
                const timestamp = resolvedOptions.now();
                const revisionNumber = 1;
                const repo = {
                    repoId: input.repoId,
                    schemaVersion: input.schemaVersion,
                    graphVersion: input.graphVersion,
                    schemaFingerprint: input.schemaFingerprint,
                    schemaFingerprintAlgorithm: input.schemaFingerprintAlgorithm,
                    defaultBranch: input.defaultBranch,
                    storageMode: input.storageMode,
                    nextRevision: input.commit ? 2 : 1,
                    createdAt: timestamp,
                    updatedAt: timestamp
                };
                const repoDocument = {
                    id: input.repoId,
                    schemaVersion: input.schemaVersion,
                    graphVersion: input.graphVersion,
                    schemaFingerprint: input.schemaFingerprint,
                    schemaFingerprintAlgorithm: input.schemaFingerprintAlgorithm,
                    defaultBranch: input.defaultBranch,
                    nextRevision: repo.nextRevision,
                    storageMode: input.storageMode,
                    checkpointEvery: resolvedOptions.checkpointEvery,
                    createdAt: timestamp,
                    updatedAt: timestamp
                };
                const revision = input.commit === true
                    ? createRevisionRecord({
                        repo,
                        revision: revisionNumber,
                        branchName: input.defaultBranch,
                        parentRevision: null,
                        stateHash: input.stateHash,
                        isEmptyRevision: false,
                        timestamp,
                        message: input.message,
                        author: input.author
                    })
                    : undefined;
                const headRevision = revision?.revision ?? null;
                const branch = createBranchRecord({
                    repoId: input.repoId,
                    branchName: input.defaultBranch,
                    revision: headRevision,
                    stateHash: input.stateHash,
                    status: input.commit ? "clean" : "dirty",
                    createdFromRevision: headRevision,
                    timestamp,
                    author: input.author
                });
                const head = createHeadRecord({
                    repoId: input.repoId,
                    branchName: input.defaultBranch,
                    status: input.commit ? "clean" : "dirty",
                    revision: headRevision,
                    baseRevision: headRevision,
                    stateHash: input.stateHash,
                    state: input.initialState,
                    timestamp,
                    author: input.author
                });
                transaction.set(repoReference, repoDocument);
                transaction.set(refs.branch(input.repoId, input.defaultBranch), branch);
                writeHeadDocument(transaction, refs, resolvedOptions, input.repoId, head);
                if (revision !== undefined) {
                    writeSnapshotRevision(transaction, refs, input.repoId, revision, input.initialState, timestamp);
                }
                return {
                    repo,
                    head,
                    ...(revision === undefined ? {} : { revision })
                };
            });
        },
        async getBranch(input) {
            const branchSnapshot = await getDoc(refs.branch(input.repoId, input.branchName));
            if (!branchSnapshot.exists()) {
                return null;
            }
            return branchRecordFromDocument(readDocumentData(branchSnapshot));
        },
        async getHead(input) {
            const headSnapshot = await getDoc(refs.head(input.repoId, input.branchName));
            if (!headSnapshot.exists()) {
                return null;
            }
            return headFromDocument(input.repoId, readDocumentData(headSnapshot), async (blobRef) => readBlobState(refs, input.repoId, blobRef));
        },
        async listBranches(input) {
            await readRepoOrThrow(refs, input.repoId);
            const snapshots = await getDocs(refs.branches(input.repoId));
            const branches = await Promise.all(snapshots.docs.map(async (snapshot) => {
                const branch = branchRecordFromDocument(readDocumentData(snapshot));
                const headSnapshot = await getDoc(refs.head(input.repoId, branch.name));
                if (!headSnapshot.exists()) {
                    return branch;
                }
                const headBlobRef = headBlobRefFromDocument(readDocumentData(headSnapshot));
                return headBlobRef === null
                    ? branch
                    : {
                        ...branch,
                        headBlobRef
                    };
            }));
            return branches.sort((left, right) => left.name.localeCompare(right.name));
        },
        async writeHead(input) {
            return runTransaction(resolvedOptions.db, async (transaction) => {
                await readRepoOrThrowInTransaction(transaction, refs, input.repoId);
                const branchSnapshot = await transaction.get(refs.branch(input.repoId, input.branchName));
                const headSnapshot = await transaction.get(refs.head(input.repoId, input.branchName));
                const currentHead = await headFromSnapshotOrThrowInTransaction(transaction, refs, input.repoId, input.branchName, headSnapshot);
                branchFromSnapshotOrThrow(input.branchName, branchSnapshot);
                checkExpectedHeadHash(currentHead, input.expectedHeadHash);
                const timestamp = resolvedOptions.now();
                const baseRevision = input.baseRevision ??
                    (currentHead.status === "clean"
                        ? currentHead.headRevision
                        : currentHead.baseRevision);
                const head = createHeadRecord({
                    repoId: input.repoId,
                    branchName: input.branchName,
                    status: "dirty",
                    revision: null,
                    baseRevision,
                    stateHash: input.stateHash,
                    state: input.state,
                    timestamp,
                    author: input.author
                });
                const branch = updateBranchRecord({
                    previous: branchRecordFromDocument(readDocumentData(branchSnapshot)),
                    revision: null,
                    baseRevision,
                    stateHash: input.stateHash,
                    status: "dirty",
                    timestamp,
                    author: input.author
                });
                transaction.set(refs.branch(input.repoId, input.branchName), branch);
                writeHeadDocument(transaction, refs, resolvedOptions, input.repoId, head);
                touchRepoDocument(transaction, refs, input.repoId, timestamp);
                return { head };
            });
        },
        async createRevision(input) {
            return runTransaction(resolvedOptions.db, async (transaction) => {
                const repo = await readRepoOrThrowInTransaction(transaction, refs, input.repoId);
                const branchSnapshot = await transaction.get(refs.branch(input.repoId, input.branchName));
                const headSnapshot = await transaction.get(refs.head(input.repoId, input.branchName));
                const currentHead = await headFromSnapshotOrThrowInTransaction(transaction, refs, input.repoId, input.branchName, headSnapshot);
                const currentBranch = branchFromSnapshotOrThrow(input.branchName, branchSnapshot);
                checkExpectedHeadHash(currentHead, input.expectedHeadHash);
                if (currentHead.status === "clean" &&
                    currentHead.headRevision !== null &&
                    currentHead.stateHash === input.stateHash &&
                    input.allowEmpty !== true) {
                    const currentRevisionSnapshot = await transaction.get(refs.revision(input.repoId, currentHead.headRevision));
                    return {
                        revision: revisionFromSnapshotOrThrow(currentHead.headRevision, currentRevisionSnapshot, input.repoId),
                        head: currentHead,
                        created: false
                    };
                }
                const revisionNumber = repo.nextRevision;
                const timestamp = resolvedOptions.now();
                const parentRevision = currentHead.status === "clean"
                    ? currentHead.headRevision
                    : currentHead.baseRevision;
                const parentHash = parentRevision === null
                    ? null
                    : revisionFromSnapshotOrThrow(parentRevision, await transaction.get(refs.revision(input.repoId, parentRevision)), input.repoId).stateHash;
                const revision = createRevisionRecord({
                    repo,
                    schemaVersion: input.schemaVersion ?? repo.schemaVersion,
                    graphIdentity: input.graphIdentity ?? {
                        graphVersion: input.graphVersion ?? repo.graphVersion,
                        schemaFingerprint: repo.schemaFingerprint,
                        schemaFingerprintAlgorithm: repo.schemaFingerprintAlgorithm
                    },
                    revision: revisionNumber,
                    branchName: input.branchName,
                    parentRevision,
                    stateHash: input.stateHash,
                    isEmptyRevision: parentHash === input.stateHash,
                    timestamp,
                    message: input.message,
                    author: input.author
                });
                const head = createHeadRecord({
                    repoId: input.repoId,
                    branchName: input.branchName,
                    status: "clean",
                    revision: revisionNumber,
                    baseRevision: revisionNumber,
                    stateHash: input.stateHash,
                    state: input.state,
                    timestamp,
                    author: input.author
                });
                const branch = updateBranchRecord({
                    previous: currentBranch,
                    revision: revisionNumber,
                    baseRevision: revisionNumber,
                    stateHash: input.stateHash,
                    status: "clean",
                    timestamp,
                    author: input.author
                });
                transaction.update(refs.repo(input.repoId), {
                    schemaVersion: input.schemaVersion ?? repo.schemaVersion,
                    graphVersion: input.graphIdentity?.graphVersion ??
                        input.graphVersion ??
                        repo.graphVersion,
                    schemaFingerprint: input.graphIdentity?.schemaFingerprint ?? repo.schemaFingerprint,
                    schemaFingerprintAlgorithm: input.graphIdentity?.schemaFingerprintAlgorithm ??
                        repo.schemaFingerprintAlgorithm,
                    nextRevision: revisionNumber + 1,
                    updatedAt: timestamp
                });
                transaction.set(refs.branch(input.repoId, input.branchName), branch);
                writeHeadDocument(transaction, refs, resolvedOptions, input.repoId, head);
                writeSnapshotRevision(transaction, refs, input.repoId, revision, input.state, timestamp);
                return {
                    revision,
                    head,
                    created: true
                };
            });
        },
        async readRevision(input) {
            await readRepoOrThrow(refs, input.repoId);
            const revisionSnapshot = await getDoc(refs.revision(input.repoId, input.revision));
            if (!revisionSnapshot.exists()) {
                return null;
            }
            const revision = revisionRecordFromDocument(input.repoId, readDocumentData(revisionSnapshot));
            const state = await readRevisionSnapshotState(refs, input.repoId, revision);
            return {
                revision,
                state
            };
        },
        async readRevisionState(input) {
            const storedRevision = await this.readRevision(input);
            return storedRevision?.state ?? null;
        },
        async listRevisions(input) {
            await readRepoOrThrow(refs, input.repoId);
            const snapshots = await getDocs(refs.revisions(input.repoId));
            const revisions = snapshots.docs
                .map(snapshot => revisionRecordFromDocument(input.repoId, readDocumentData(snapshot)))
                .filter(revision => input.branchName === undefined ||
                revision.branchName === input.branchName)
                .filter(revision => input.after === undefined || revision.revision > input.after)
                .sort((left, right) => input.order === "asc"
                ? left.revision - right.revision
                : right.revision - left.revision);
            return revisions.slice(0, input.limit);
        },
        async createTag(input) {
            return runTransaction(resolvedOptions.db, async (transaction) => {
                const repo = await readRepoOrThrowInTransaction(transaction, refs, input.repoId);
                const tagReference = refs.tag(input.repoId, input.name);
                const tagSnapshot = await transaction.get(tagReference);
                if (tagSnapshot.exists() && input.overwrite !== true) {
                    throw new TagAlreadyExistsError(`Tag "${input.name}" already exists.`);
                }
                const branchName = input.branchName ?? repo.defaultBranch;
                let revision = input.revision === undefined || input.revision === "HEAD"
                    ? null
                    : input.revision;
                let revisionToWrite = null;
                let revisionStateToWrite = null;
                let branchToWrite = null;
                let headToWrite = null;
                if (input.revision === undefined || input.revision === "HEAD") {
                    const head = await headFromSnapshotOrThrowInTransaction(transaction, refs, input.repoId, branchName, await transaction.get(refs.head(input.repoId, branchName)));
                    checkExpectedHeadHash(head, input.expectedHeadHash);
                    if (head.status === "dirty") {
                        if (input.createRevisionIfDirty === false) {
                            throw new DirtyHeadError("Cannot tag a dirty HEAD when createRevisionIfDirty is false.");
                        }
                        const branchSnapshot = await transaction.get(refs.branch(input.repoId, branchName));
                        const branch = branchFromSnapshotOrThrow(branchName, branchSnapshot);
                        const revisionNumber = repo.nextRevision;
                        const timestamp = resolvedOptions.now();
                        const parentRevision = head.baseRevision;
                        const parentHash = parentRevision === null
                            ? null
                            : revisionFromSnapshotOrThrow(parentRevision, await transaction.get(refs.revision(input.repoId, parentRevision)), input.repoId).stateHash;
                        revisionToWrite = createRevisionRecord({
                            repo,
                            revision: revisionNumber,
                            branchName,
                            parentRevision,
                            stateHash: head.stateHash,
                            isEmptyRevision: parentHash === head.stateHash,
                            timestamp,
                            message: `Create revision for tag ${input.name}`,
                            author: input.author
                        });
                        revisionStateToWrite = head.state;
                        headToWrite = createHeadRecord({
                            repoId: input.repoId,
                            branchName,
                            status: "clean",
                            revision: revisionNumber,
                            baseRevision: revisionNumber,
                            stateHash: head.stateHash,
                            state: head.state,
                            timestamp,
                            author: input.author
                        });
                        branchToWrite = updateBranchRecord({
                            previous: branch,
                            revision: revisionNumber,
                            baseRevision: revisionNumber,
                            stateHash: head.stateHash,
                            status: "clean",
                            timestamp,
                            author: input.author
                        });
                        revision = revisionNumber;
                    }
                    else {
                        revision = head.headRevision;
                    }
                }
                if (revision === null) {
                    throw new RevisionNotFoundError("Cannot tag a dirty HEAD directly.");
                }
                if (revisionToWrite === null) {
                    revisionFromSnapshotOrThrow(revision, await transaction.get(refs.revision(input.repoId, revision)), input.repoId);
                }
                const tag = {
                    repoId: input.repoId,
                    name: input.name,
                    revision,
                    ...(input.annotation === undefined
                        ? {}
                        : { annotation: input.annotation }),
                    createdAt: resolvedOptions.now(),
                    ...(input.author === undefined ? {} : { createdBy: input.author })
                };
                if (revisionToWrite !== null &&
                    revisionStateToWrite !== null &&
                    branchToWrite !== null &&
                    headToWrite !== null) {
                    transaction.update(refs.repo(input.repoId), {
                        nextRevision: revisionToWrite.revision + 1,
                        updatedAt: revisionToWrite.createdAt
                    });
                    transaction.set(refs.branch(input.repoId, branchName), branchToWrite);
                    writeHeadDocument(transaction, refs, resolvedOptions, input.repoId, headToWrite);
                    writeSnapshotRevision(transaction, refs, input.repoId, revisionToWrite, revisionStateToWrite, revisionToWrite.createdAt);
                }
                else {
                    touchRepoDocument(transaction, refs, input.repoId, resolvedOptions.now());
                }
                transaction.set(tagReference, tag);
                return tag;
            });
        },
        async listTags(input) {
            await readRepoOrThrow(refs, input.repoId);
            const snapshots = await getDocs(refs.tags(input.repoId));
            return snapshots.docs
                .map(snapshot => tagRecordFromDocument(readDocumentData(snapshot)))
                .sort((left, right) => left.name.localeCompare(right.name));
        },
        async deleteTag(input) {
            return runTransaction(resolvedOptions.db, async (transaction) => {
                await readRepoOrThrowInTransaction(transaction, refs, input.repoId);
                const tagReference = refs.tag(input.repoId, input.name);
                const tagSnapshot = await transaction.get(tagReference);
                if (!tagSnapshot.exists()) {
                    if ((input.missing ?? "throw") === "ignore") {
                        return {
                            deleted: false,
                            name: input.name,
                            previousRevision: null
                        };
                    }
                    throw new TagNotFoundError(`Tag "${input.name}" was not found.`);
                }
                const tag = tagRecordFromDocument(readDocumentData(tagSnapshot));
                if (input.expectedRevision !== undefined &&
                    tag.revision !== input.expectedRevision) {
                    throw new TagRevisionMismatchError(`Tag "${input.name}" points to revision "${tag.revision}", not "${input.expectedRevision}".`);
                }
                transaction.delete(tagReference);
                touchRepoDocument(transaction, refs, input.repoId, resolvedOptions.now());
                return {
                    deleted: true,
                    name: input.name,
                    previousRevision: tag.revision
                };
            });
        },
        async planGarbageCollection(input) {
            return planFirebaseGarbageCollection({
                refs,
                options: resolvedOptions,
                input
            });
        },
        async runGarbageCollection(input) {
            const startedAt = resolvedOptions.now();
            const recomputedPlan = await planFirebaseGarbageCollection({
                refs,
                options: resolvedOptions,
                input: {
                    repoId: input.repoId,
                    ...(input.plan.options.beforeRevision === null
                        ? {}
                        : { beforeRevision: input.plan.options.beforeRevision }),
                    keepTagged: true,
                    keepBranchHeads: true,
                    keepDirtyBaseRevisions: true,
                    includeOrphanBlobs: input.plan.options.includeOrphanBlobs,
                    protectRevisions: input.plan.options.protectRevisions,
                    ...(input.plan.options.maxRevisionsToDelete === null
                        ? {}
                        : {
                            maxRevisionsToDelete: input.plan.options.maxRevisionsToDelete
                        }),
                    estimateStorage: input.plan.options.estimateStorage
                }
            });
            if (recomputedPlan.refsSnapshotHash !== input.plan.refsSnapshotHash) {
                throw new GarbageCollectionPlanStaleError("Garbage collection plan is stale. Recompute the plan before running it.");
            }
            const dryRun = input.dryRun ?? false;
            if (dryRun) {
                return {
                    planId: input.plan.planId,
                    repoId: input.repoId,
                    dryRun: true,
                    deletedRevisions: [],
                    deletedBlobs: [],
                    skippedRevisions: recomputedPlan.blockedRevisions,
                    skippedBlobs: [],
                    freedStorageEstimate: zeroStorageEstimate(),
                    startedAt,
                    completedAt: resolvedOptions.now()
                };
            }
            const deletedRevisions = [];
            const skippedRevisions = [
                ...recomputedPlan.blockedRevisions
            ];
            for (const chunk of chunkItems(recomputedPlan.deletableRevisions, 150)) {
                const latestPlan = await planFirebaseGarbageCollection({
                    refs,
                    options: resolvedOptions,
                    input: {
                        repoId: input.repoId,
                        ...(input.plan.options.beforeRevision === null
                            ? {}
                            : { beforeRevision: input.plan.options.beforeRevision }),
                        keepTagged: true,
                        keepBranchHeads: true,
                        keepDirtyBaseRevisions: true,
                        includeOrphanBlobs: input.plan.options.includeOrphanBlobs,
                        protectRevisions: input.plan.options.protectRevisions,
                        ...(input.plan.options.maxRevisionsToDelete === null
                            ? {}
                            : {
                                maxRevisionsToDelete: input.plan.options.maxRevisionsToDelete
                            }),
                        estimateStorage: input.plan.options.estimateStorage
                    }
                });
                if (latestPlan.refsSnapshotHash !== input.plan.refsSnapshotHash) {
                    throw new GarbageCollectionPlanStaleError("Garbage collection plan is stale. Recompute the plan before running it.");
                }
                const latestDeletable = new Set(latestPlan.deletableRevisions.map(revision => revision.revision));
                const chunkResult = await runTransaction(resolvedOptions.db, async (transaction) => {
                    const chunkDeletedRevisions = [];
                    const chunkSkippedRevisions = [];
                    await readRepoOrThrowInTransaction(transaction, refs, input.repoId);
                    for (const revision of chunk) {
                        if (!latestDeletable.has(revision.revision)) {
                            chunkSkippedRevisions.push({
                                revision: revision.revision,
                                reasons: ["missing-metadata"]
                            });
                            continue;
                        }
                        const revisionSnapshot = await transaction.get(refs.revision(input.repoId, revision.revision));
                        if (!revisionSnapshot.exists()) {
                            chunkSkippedRevisions.push({
                                revision: revision.revision,
                                reasons: ["missing-metadata"]
                            });
                            continue;
                        }
                        transaction.delete(refs.revision(input.repoId, revision.revision));
                        chunkDeletedRevisions.push(revision.revision);
                    }
                    touchRepoDocument(transaction, refs, input.repoId, resolvedOptions.now());
                    return {
                        deletedRevisions: chunkDeletedRevisions,
                        skippedRevisions: chunkSkippedRevisions
                    };
                });
                deletedRevisions.push(...chunkResult.deletedRevisions);
                skippedRevisions.push(...chunkResult.skippedRevisions);
            }
            const deletedBlobs = await deleteGarbageCollectionBlobs({
                refs,
                options: resolvedOptions,
                repoId: input.repoId,
                plan: recomputedPlan
            });
            return {
                planId: input.plan.planId,
                repoId: input.repoId,
                dryRun: false,
                deletedRevisions,
                deletedBlobs: deletedBlobs.deleted,
                skippedRevisions,
                skippedBlobs: deletedBlobs.skipped,
                freedStorageEstimate: recomputedPlan.estimatedFreedStorage,
                startedAt,
                completedAt: resolvedOptions.now()
            };
        },
        async estimateStorage(input) {
            return estimateFirebaseStorage({
                refs,
                options: resolvedOptions,
                input
            });
        },
        async createBranch(input) {
            return runTransaction(resolvedOptions.db, async (transaction) => {
                const repo = await readRepoOrThrowInTransaction(transaction, refs, input.repoId);
                const branchReference = refs.branch(input.repoId, input.name);
                const branchSnapshot = await transaction.get(branchReference);
                if (branchSnapshot.exists()) {
                    throw new BranchAlreadyExistsError(`Branch "${input.name}" already exists.`);
                }
                const sourceRevision = input.from === "HEAD"
                    ? (await headFromSnapshotOrThrowInTransaction(transaction, refs, input.repoId, input.sourceBranch ?? repo.defaultBranch, await transaction.get(refs.head(input.repoId, input.sourceBranch ?? repo.defaultBranch)))).headRevision
                    : input.from;
                if (sourceRevision === null) {
                    throw new RevisionNotFoundError("Cannot create a branch from a dirty HEAD.");
                }
                const revisionSnapshot = await transaction.get(refs.revision(input.repoId, sourceRevision));
                const revision = revisionFromSnapshotOrThrow(sourceRevision, revisionSnapshot, input.repoId);
                const sourceState = await readRevisionSnapshotStateInTransaction(transaction, refs, input.repoId, revision);
                const timestamp = resolvedOptions.now();
                const branch = createBranchRecord({
                    repoId: input.repoId,
                    branchName: input.name,
                    revision: sourceRevision,
                    stateHash: revision.stateHash,
                    status: "clean",
                    createdFromRevision: sourceRevision,
                    timestamp,
                    author: input.author
                });
                const head = createHeadRecord({
                    repoId: input.repoId,
                    branchName: input.name,
                    status: "clean",
                    revision: sourceRevision,
                    baseRevision: sourceRevision,
                    stateHash: revision.stateHash,
                    state: sourceState,
                    timestamp,
                    author: input.author
                });
                transaction.set(branchReference, branch);
                writeHeadDocument(transaction, refs, resolvedOptions, input.repoId, head);
                touchRepoDocument(transaction, refs, input.repoId, timestamp);
                return branch;
            });
        },
        async updateBranch(input) {
            return runTransaction(resolvedOptions.db, async (transaction) => {
                await readRepoOrThrowInTransaction(transaction, refs, input.repoId);
                const branchSnapshot = await transaction.get(refs.branch(input.repoId, input.branchName));
                const branch = updateBranchRecord({
                    previous: branchFromSnapshotOrThrow(input.branchName, branchSnapshot),
                    revision: input.headRevision,
                    baseRevision: input.baseRevision,
                    stateHash: input.headStateHash,
                    status: input.status,
                    timestamp: resolvedOptions.now(),
                    author: input.author
                });
                transaction.set(refs.branch(input.repoId, input.branchName), branch);
                touchRepoDocument(transaction, refs, input.repoId, resolvedOptions.now());
                return branch;
            });
        },
        async restoreRevision(input) {
            if (input.commit === true) {
                const result = await this.createRevision({
                    repoId: input.repoId,
                    branchName: input.branchName,
                    state: input.state,
                    stateHash: input.stateHash,
                    ...(input.message === undefined ? {} : { message: input.message }),
                    ...(input.author === undefined ? {} : { author: input.author }),
                    ...(input.expectedHeadHash === undefined
                        ? {}
                        : { expectedHeadHash: input.expectedHeadHash })
                });
                return { head: result.head };
            }
            return this.writeHead({
                repoId: input.repoId,
                branchName: input.branchName,
                state: input.state,
                stateHash: input.stateHash,
                ...(input.author === undefined ? {} : { author: input.author }),
                ...(input.expectedHeadHash === undefined
                    ? {}
                    : { expectedHeadHash: input.expectedHeadHash })
            });
        },
        async resetBranch(input) {
            if (input.mode !== "hard") {
                throw new PersistenceError('resetBranch only supports mode "hard".');
            }
            return runTransaction(resolvedOptions.db, async (transaction) => {
                await readRepoOrThrowInTransaction(transaction, refs, input.repoId);
                const branchSnapshot = await transaction.get(refs.branch(input.repoId, input.branchName));
                const headSnapshot = await transaction.get(refs.head(input.repoId, input.branchName));
                const head = await headFromSnapshotOrThrowInTransaction(transaction, refs, input.repoId, input.branchName, headSnapshot);
                checkExpectedHeadHash(head, input.expectedHeadHash);
                const revisionSnapshot = await transaction.get(refs.revision(input.repoId, input.to));
                const revision = revisionFromSnapshotOrThrow(input.to, revisionSnapshot, input.repoId);
                const state = await readRevisionSnapshotStateInTransaction(transaction, refs, input.repoId, revision);
                const timestamp = resolvedOptions.now();
                const branch = updateBranchRecord({
                    previous: branchFromSnapshotOrThrow(input.branchName, branchSnapshot),
                    revision: input.to,
                    baseRevision: input.to,
                    stateHash: revision.stateHash,
                    status: "clean",
                    timestamp,
                    author: input.author
                });
                const nextHead = createHeadRecord({
                    repoId: input.repoId,
                    branchName: input.branchName,
                    status: "clean",
                    revision: input.to,
                    baseRevision: input.to,
                    stateHash: revision.stateHash,
                    state,
                    timestamp,
                    author: input.author
                });
                transaction.set(refs.branch(input.repoId, input.branchName), branch);
                writeHeadDocument(transaction, refs, resolvedOptions, input.repoId, nextHead);
                touchRepoDocument(transaction, refs, input.repoId, timestamp);
                return branch;
            });
        },
        subscribeHead(input, callback) {
            return onSnapshot(refs.head(input.repoId, input.branchName), snapshot => {
                if (!snapshot.exists()) {
                    return;
                }
                void headFromDocument(input.repoId, readDocumentData(snapshot), async (blobRef) => readBlobState(refs, input.repoId, blobRef)).then(callback);
            });
        },
        subscribeRevisions(input, callback) {
            return onSnapshot(refs.revisions(input.repoId), snapshot => {
                const revisions = snapshot.docs
                    .map(item => revisionRecordFromDocument(input.repoId, readDocumentData(item)))
                    .filter(revision => input.branchName === undefined ||
                    revision.branchName === input.branchName)
                    .filter(revision => input.after === undefined || revision.revision > input.after)
                    .sort((left, right) => input.order === "asc"
                    ? left.revision - right.revision
                    : right.revision - left.revision)
                    .slice(0, input.limit);
                callback(revisions);
            });
        },
        subscribeTags(input, callback) {
            return onSnapshot(refs.tags(input.repoId), snapshot => {
                const tags = snapshot.docs
                    .map(item => tagRecordFromDocument(readDocumentData(item)))
                    .sort((left, right) => left.name.localeCompare(right.name));
                callback(tags);
            });
        },
        subscribeBranches(input, callback) {
            return onSnapshot(refs.branches(input.repoId), snapshot => {
                const branches = snapshot.docs
                    .map(item => branchRecordFromDocument(readDocumentData(item)))
                    .sort((left, right) => left.name.localeCompare(right.name));
                callback(branches);
            });
        }
    };
}
export const objectVcsFirebasePackage = "@bjalon/object-vcs-firebase";
function resolveOptions(options) {
    return {
        db: options.db,
        rootCollection: options.rootCollection ?? "objectVcs",
        collections: {
            branches: options.collections?.branches ?? "branches",
            heads: options.collections?.heads ?? "heads",
            revisions: options.collections?.revisions ?? "revisions",
            tags: options.collections?.tags ?? "tags",
            blobs: options.collections?.blobs ?? "blobs"
        },
        checkpointEvery: options.checkpointEvery ?? 1,
        maxInlineHeadStateBytes: options.maxInlineHeadStateBytes ?? 900_000,
        storageEstimate: {
            fixedBytesPerDocument: options.storageEstimate?.fixedBytesPerDocument ?? 512,
            fixedBytesPerRevision: options.storageEstimate?.fixedBytesPerRevision ?? 256,
            fixedBytesPerBlob: options.storageEstimate?.fixedBytesPerBlob ?? 128
        },
        now: options.now ?? (() => new Date().toISOString())
    };
}
function createReferenceFactory(options) {
    return {
        repo(repoId) {
            return doc(collection(options.db, options.rootCollection), encodeId(repoId));
        },
        branches(repoId) {
            return collection(this.repo(repoId), options.collections.branches);
        },
        branch(repoId, branchName) {
            return doc(this.branches(repoId), encodeId(branchName));
        },
        heads(repoId) {
            return collection(this.repo(repoId), options.collections.heads);
        },
        head(repoId, branchName) {
            return doc(this.heads(repoId), encodeId(branchName));
        },
        revisions(repoId) {
            return collection(this.repo(repoId), options.collections.revisions);
        },
        revision(repoId, revision) {
            return doc(this.revisions(repoId), String(revision));
        },
        tags(repoId) {
            return collection(this.repo(repoId), options.collections.tags);
        },
        tag(repoId, name) {
            return doc(this.tags(repoId), encodeId(name));
        },
        blobs(repoId) {
            return collection(this.repo(repoId), options.collections.blobs);
        },
        blob(repoId, stateHash) {
            return doc(this.blobs(repoId), encodeId(stateHash));
        }
    };
}
function encodeId(value) {
    if (value.length === 0) {
        throw new PersistenceError("Firestore document identifiers cannot be empty.");
    }
    return Array.from(new TextEncoder().encode(value))
        .map(byte => byte.toString(16).padStart(2, "0"))
        .join("");
}
function decodeId(value) {
    if (value.length % 2 !== 0 || !/^[0-9a-f]*$/i.test(value)) {
        throw new PersistenceError(`Firestore document identifier "${value}" is not valid hex.`);
    }
    const bytes = [];
    for (let index = 0; index < value.length; index += 2) {
        const byte = Number.parseInt(value.slice(index, index + 2), 16);
        bytes.push(byte);
    }
    return new TextDecoder().decode(new Uint8Array(bytes));
}
function readDocumentData(snapshot) {
    const data = snapshot.data();
    if (!isRecord(data)) {
        throw new PersistenceError("Firestore document is not a plain object.");
    }
    return data;
}
async function readRepoOrThrow(refs, repoId) {
    const snapshot = await getDoc(refs.repo(repoId));
    if (!snapshot.exists()) {
        throw new RepositoryNotFoundError(`Repository "${repoId}" was not found.`);
    }
    return repoRecordFromDocument(repoId, readDocumentData(snapshot));
}
async function readRepoOrThrowInTransaction(transaction, refs, repoId) {
    const snapshot = await transaction.get(refs.repo(repoId));
    if (!snapshot.exists()) {
        throw new RepositoryNotFoundError(`Repository "${repoId}" was not found.`);
    }
    return repoRecordFromDocument(repoId, readDocumentData(snapshot));
}
function repoRecordFromDocument(repoId, data) {
    const document = data;
    return {
        repoId,
        schemaVersion: document.schemaVersion,
        graphVersion: document.graphVersion,
        schemaFingerprint: document.schemaFingerprint ?? `legacy:${document.graphVersion}`,
        schemaFingerprintAlgorithm: document.schemaFingerprintAlgorithm ?? "manual",
        defaultBranch: document.defaultBranch,
        storageMode: document.storageMode,
        nextRevision: document.nextRevision,
        createdAt: document.createdAt,
        updatedAt: document.updatedAt
    };
}
function branchRecordFromDocument(data) {
    return data;
}
function headBlobRefFromDocument(data) {
    const document = data;
    return typeof document.stateBlobRef === "string" ? document.stateBlobRef : null;
}
function tagRecordFromDocument(data) {
    return data;
}
function revisionRecordFromDocument(repoId, data) {
    const document = data;
    return {
        repoId,
        revision: document.revision,
        parentRevision: document.parentRevision,
        branchName: document.branchName,
        stateHash: document.stateHash,
        schemaVersion: document.schemaVersion,
        graphVersion: document.graphVersion,
        schemaFingerprint: document.schemaFingerprint ?? `legacy:${document.graphVersion}`,
        schemaFingerprintAlgorithm: document.schemaFingerprintAlgorithm ?? "manual",
        ...(document.message === undefined ? {} : { message: document.message }),
        createdAt: document.createdAt,
        ...(document.createdBy === undefined
            ? {}
            : { createdBy: document.createdBy }),
        isEmptyRevision: document.isEmptyRevision,
        isCheckpoint: document.isCheckpoint,
        ...(document.patchBlobRef === undefined
            ? {}
            : { patchRef: document.patchBlobRef }),
        ...(document.snapshotBlobRef === undefined
            ? {}
            : { snapshotRef: document.snapshotBlobRef })
    };
}
async function headFromDocument(repoId, data, readStateBlob) {
    const document = data;
    const state = document.state === undefined
        ? await readStateBlob(requireString(document.stateBlobRef, "stateBlobRef"))
        : document.state;
    return {
        repoId,
        branchName: document.branchName,
        status: document.status,
        headRevision: document.headRevision,
        baseRevision: document.baseRevision,
        stateHash: document.stateHash,
        state,
        updatedAt: document.updatedAt,
        ...(document.updatedBy === undefined
            ? {}
            : { updatedBy: document.updatedBy })
    };
}
async function headFromSnapshotOrThrowInTransaction(transaction, refs, repoId, branchName, snapshot) {
    if (!snapshot.exists()) {
        throw new BranchNotFoundError(`Branch "${branchName}" was not found.`);
    }
    const data = readDocumentData(snapshot);
    const document = data;
    const state = document.state === undefined
        ? await readBlobStateInTransaction(transaction, refs, repoId, requireString(document.stateBlobRef, "stateBlobRef"))
        : document.state;
    return {
        repoId,
        branchName: document.branchName,
        status: document.status,
        headRevision: document.headRevision,
        baseRevision: document.baseRevision,
        stateHash: document.stateHash,
        state,
        updatedAt: document.updatedAt,
        ...(document.updatedBy === undefined
            ? {}
            : { updatedBy: document.updatedBy })
    };
}
function branchFromSnapshotOrThrow(branchName, snapshot) {
    if (!snapshot.exists()) {
        throw new BranchNotFoundError(`Branch "${branchName}" was not found.`);
    }
    return branchRecordFromDocument(readDocumentData(snapshot));
}
function revisionFromSnapshotOrThrow(revision, snapshot, repoId) {
    if (!snapshot.exists()) {
        throw new RevisionNotFoundError(`Revision "${revision}" was not found.`);
    }
    return revisionRecordFromDocument(repoId, readDocumentData(snapshot));
}
function createBranchRecord(input) {
    return {
        repoId: input.repoId,
        name: input.branchName,
        headRevision: input.revision,
        baseRevision: input.revision,
        headStateHash: input.stateHash,
        status: input.status,
        createdFromRevision: input.createdFromRevision,
        createdAt: input.timestamp,
        updatedAt: input.timestamp,
        ...(input.author === undefined ? {} : { createdBy: input.author }),
        ...(input.author === undefined ? {} : { updatedBy: input.author })
    };
}
function updateBranchRecord(input) {
    return {
        ...input.previous,
        headRevision: input.revision,
        baseRevision: input.baseRevision,
        headStateHash: input.stateHash,
        status: input.status,
        updatedAt: input.timestamp,
        ...(input.author === undefined ? {} : { updatedBy: input.author })
    };
}
function createHeadRecord(input) {
    return {
        repoId: input.repoId,
        branchName: input.branchName,
        status: input.status,
        headRevision: input.revision,
        baseRevision: input.baseRevision,
        stateHash: input.stateHash,
        state: input.state,
        updatedAt: input.timestamp,
        ...(input.author === undefined ? {} : { updatedBy: input.author })
    };
}
function createRevisionRecord(input) {
    return {
        repoId: input.repo.repoId,
        revision: input.revision,
        parentRevision: input.parentRevision,
        branchName: input.branchName,
        stateHash: input.stateHash,
        schemaVersion: input.schemaVersion ?? input.repo.schemaVersion,
        graphVersion: input.graphIdentity?.graphVersion ?? input.repo.graphVersion,
        schemaFingerprint: input.graphIdentity?.schemaFingerprint ?? input.repo.schemaFingerprint,
        schemaFingerprintAlgorithm: input.graphIdentity?.schemaFingerprintAlgorithm ??
            input.repo.schemaFingerprintAlgorithm,
        ...(input.message === undefined ? {} : { message: input.message }),
        createdAt: input.timestamp,
        ...(input.author === undefined ? {} : { createdBy: input.author }),
        isEmptyRevision: input.isEmptyRevision,
        isCheckpoint: true,
        snapshotRef: createRevisionBlobRef(input.stateHash, input.revision)
    };
}
function createRevisionBlobRef(stateHash, revision) {
    return `${stateHash}:revision:${revision}`;
}
function createHeadBlobRef(head) {
    return `${head.stateHash}:head:${head.branchName}:${head.updatedAt}`;
}
function writeHeadDocument(transaction, refs, options, repoId, head) {
    const inlineState = jsonByteSize(head.state) <= options.maxInlineHeadStateBytes;
    const stateBlobRef = createHeadBlobRef(head);
    const document = {
        branchName: head.branchName,
        status: head.status,
        headRevision: head.headRevision,
        baseRevision: head.baseRevision,
        stateHash: head.stateHash,
        ...(inlineState ? { state: head.state } : { stateBlobRef }),
        updatedAt: head.updatedAt,
        ...(head.updatedBy === undefined ? {} : { updatedBy: head.updatedBy })
    };
    if (!inlineState) {
        writeBlob(transaction, refs, repoId, stateBlobRef, head.stateHash, head.state, head.updatedAt);
    }
    transaction.set(refs.head(repoId, head.branchName), document);
}
function writeSnapshotRevision(transaction, refs, repoId, revision, state, timestamp) {
    const document = {
        revision: revision.revision,
        parentRevision: revision.parentRevision,
        branchName: revision.branchName,
        stateHash: revision.stateHash,
        schemaVersion: revision.schemaVersion,
        graphVersion: revision.graphVersion,
        schemaFingerprint: revision.schemaFingerprint,
        schemaFingerprintAlgorithm: revision.schemaFingerprintAlgorithm,
        snapshotBlobRef: requireString(revision.snapshotRef, "snapshotRef"),
        isCheckpoint: revision.isCheckpoint,
        isEmptyRevision: revision.isEmptyRevision,
        ...(revision.message === undefined ? {} : { message: revision.message }),
        createdAt: revision.createdAt,
        ...(revision.createdBy === undefined
            ? {}
            : { createdBy: revision.createdBy })
    };
    writeBlob(transaction, refs, repoId, requireString(revision.snapshotRef, "snapshotRef"), revision.stateHash, state, timestamp);
    transaction.set(refs.revision(repoId, revision.revision), document);
}
function writeBlob(transaction, refs, repoId, blobRef, stateHash, state, timestamp) {
    const document = {
        blobRef,
        kind: "snapshot",
        stateHash,
        state,
        createdAt: timestamp
    };
    transaction.set(refs.blob(repoId, blobRef), document);
}
function touchRepoDocument(transaction, refs, repoId, timestamp) {
    transaction.update(refs.repo(repoId), {
        updatedAt: timestamp
    });
}
async function readRevisionSnapshotState(refs, repoId, revision) {
    return readBlobState(refs, repoId, requireString(revision.snapshotRef, "snapshotRef"));
}
async function readRevisionSnapshotStateInTransaction(transaction, refs, repoId, revision) {
    return readBlobStateInTransaction(transaction, refs, repoId, requireString(revision.snapshotRef, "snapshotRef"));
}
async function readBlobState(refs, repoId, blobRef) {
    const snapshot = await getDoc(refs.blob(repoId, blobRef));
    if (!snapshot.exists()) {
        throw new RevisionNotFoundError(`Snapshot blob "${blobRef}" was not found.`);
    }
    const document = readDocumentData(snapshot);
    return document.state;
}
async function readBlobStateInTransaction(transaction, refs, repoId, blobRef) {
    const snapshot = await transaction.get(refs.blob(repoId, blobRef));
    if (!snapshot.exists()) {
        throw new RevisionNotFoundError(`Snapshot blob "${blobRef}" was not found.`);
    }
    const document = readDocumentData(snapshot);
    return document.state;
}
async function planFirebaseGarbageCollection(input) {
    await readRepoOrThrow(input.refs, input.input.repoId);
    const normalizedOptions = normalizeGarbageCollectionPlanOptions(input.input);
    const revisions = (await listAllFirebaseRevisions(input.refs, input.input.repoId)).sort((left, right) => left.revision - right.revision);
    const tags = (await listAllFirebaseTags(input.refs, input.input.repoId)).sort((left, right) => left.name.localeCompare(right.name));
    const branches = (await listAllFirebaseBranches(input.refs, input.input.repoId)).sort((left, right) => left.name.localeCompare(right.name));
    const revisionMap = new Map(revisions.map(revision => [revision.revision, revision]));
    const protectedReasons = new Map();
    const blockedReasons = new Map();
    const latestRevision = revisions.length === 0 ? null : revisions[revisions.length - 1]?.revision ?? null;
    const refsSnapshot = buildRefsSnapshot({
        tags,
        branches,
        latestRevision
    });
    const refsSnapshotHash = await hashState(refsSnapshot);
    function addProtected(revision, reason) {
        const reasons = protectedReasons.get(revision) ?? new Set();
        reasons.add(reason);
        protectedReasons.set(revision, reasons);
        addBlocked(revision, reason);
    }
    function addBlocked(revision, reason) {
        const reasons = blockedReasons.get(revision) ?? new Set();
        reasons.add(reason);
        blockedReasons.set(revision, reasons);
    }
    const protectedRoots = [];
    for (const tag of tags) {
        addProtected(tag.revision, "tagged");
        protectedRoots.push(tag.revision);
    }
    for (const branch of branches) {
        if (branch.headRevision !== null) {
            addProtected(branch.headRevision, "branch-head");
            protectedRoots.push(branch.headRevision);
        }
        if (branch.baseRevision !== null) {
            addProtected(branch.baseRevision, "branch-base-revision");
            protectedRoots.push(branch.baseRevision);
            if (branch.status === "dirty") {
                addProtected(branch.baseRevision, "dirty-base-revision");
            }
        }
    }
    for (const revision of normalizedOptions.protectRevisions) {
        addProtected(revision, "explicitly-protected");
        protectedRoots.push(revision);
    }
    for (const revision of protectedRoots) {
        if (!revisionMap.has(revision)) {
            addBlocked(revision, "missing-metadata");
            continue;
        }
        markAncestorRevisions({
            startRevision: revision,
            revisionMap,
            onAncestor: ancestor => {
                addProtected(ancestor, "ancestor-of-protected-revision");
            },
            onMissingParent: missingParent => {
                addBlocked(missingParent, "missing-metadata");
            },
            onUnknownParent: child => {
                addBlocked(child, "unknown-parent");
            }
        });
    }
    for (const revision of revisions) {
        if (protectedReasons.has(revision.revision)) {
            continue;
        }
        if (normalizedOptions.beforeRevision !== null &&
            revision.revision >= normalizedOptions.beforeRevision) {
            addBlocked(revision.revision, "after-before-revision-threshold");
            markAncestorRevisions({
                startRevision: revision.revision,
                revisionMap,
                onAncestor: ancestor => {
                    if (!protectedReasons.has(ancestor)) {
                        addBlocked(ancestor, "ancestor-of-protected-revision");
                    }
                },
                onMissingParent: missingParent => {
                    addBlocked(missingParent, "missing-metadata");
                },
                onUnknownParent: child => {
                    addBlocked(child, "unknown-parent");
                }
            });
        }
    }
    const deletableRevisions = [];
    for (const revision of revisions) {
        if (blockedReasons.has(revision.revision)) {
            continue;
        }
        if (revision.parentRevision !== null &&
            !revisionMap.has(revision.parentRevision)) {
            addBlocked(revision.revision, "unknown-parent");
            continue;
        }
        if (revision.snapshotRef === undefined) {
            addBlocked(revision.revision, "missing-metadata");
            continue;
        }
        const estimatedStorage = await estimateFirebaseRevisionStorage({
            refs: input.refs,
            repoId: input.input.repoId,
            revision,
            estimateStorage: normalizedOptions.estimateStorage
        });
        if (estimatedStorage === null) {
            addBlocked(revision.revision, "missing-metadata");
            continue;
        }
        deletableRevisions.push({
            revision: revision.revision,
            parentRevision: revision.parentRevision,
            branchName: revision.branchName,
            stateHash: revision.stateHash,
            snapshotBlobRef: revision.snapshotRef,
            estimatedStorage
        });
    }
    const limitedDeletableRevisions = normalizedOptions.maxRevisionsToDelete === null
        ? deletableRevisions
        : deletableRevisions.slice(0, normalizedOptions.maxRevisionsToDelete);
    const orphanBlobs = normalizedOptions.includeOrphanBlobs
        ? await listOrphanFirebaseBlobs({
            refs: input.refs,
            repoId: input.input.repoId,
            liveBlobRefs: collectLiveBlobRefs({ revisions, branches }),
            estimateStorage: normalizedOptions.estimateStorage
        })
        : [];
    const estimatedFreedStorage = sumStorageEstimates([
        ...limitedDeletableRevisions.map(revision => revision.estimatedStorage),
        ...orphanBlobs.map(blob => blob.estimatedStorage)
    ]);
    return {
        planId: `gc:${refsSnapshotHash}:${limitedDeletableRevisions
            .map(revision => revision.revision)
            .join(",")}:${orphanBlobs.map(blob => blob.blobRef).join(",")}`,
        repoId: input.input.repoId,
        strategy: "unreachable-snapshots-v1",
        createdAt: input.options.now(),
        options: normalizedOptions,
        protectedRevisions: mapReasonRecords(protectedReasons),
        deletableRevisions: limitedDeletableRevisions,
        blockedRevisions: mapReasonRecords(blockedReasons),
        orphanBlobs,
        estimatedFreedStorage,
        refsSnapshot,
        refsSnapshotHash
    };
}
function normalizeGarbageCollectionPlanOptions(options) {
    return {
        beforeRevision: options.beforeRevision ?? null,
        keepTagged: true,
        keepBranchHeads: true,
        keepDirtyBaseRevisions: true,
        includeOrphanBlobs: options.includeOrphanBlobs ?? true,
        protectRevisions: [...(options.protectRevisions ?? [])].sort((left, right) => left - right),
        maxRevisionsToDelete: options.maxRevisionsToDelete ?? null,
        estimateStorage: options.estimateStorage ?? true
    };
}
async function listAllFirebaseRevisions(refs, repoId) {
    const snapshots = await getDocs(refs.revisions(repoId));
    return snapshots.docs.map(snapshot => revisionRecordFromDocument(repoId, readDocumentData(snapshot)));
}
async function listAllFirebaseTags(refs, repoId) {
    const snapshots = await getDocs(refs.tags(repoId));
    return snapshots.docs.map(snapshot => tagRecordFromDocument(readDocumentData(snapshot)));
}
async function listAllFirebaseBranches(refs, repoId) {
    const snapshots = await getDocs(refs.branches(repoId));
    const branches = await Promise.all(snapshots.docs.map(async (snapshot) => {
        const branch = branchRecordFromDocument(readDocumentData(snapshot));
        const headSnapshot = await getDoc(refs.head(repoId, branch.name));
        if (!headSnapshot.exists()) {
            return branch;
        }
        const headBlobRef = headBlobRefFromDocument(readDocumentData(headSnapshot));
        return headBlobRef === null ? branch : { ...branch, headBlobRef };
    }));
    return branches;
}
function buildRefsSnapshot(input) {
    return {
        tags: input.tags.map(tag => ({
            name: tag.name,
            revision: tag.revision
        })),
        branches: input.branches.map(branch => ({
            name: branch.name,
            headRevision: branch.headRevision,
            baseRevision: branch.baseRevision,
            status: branch.status,
            ...(branch.headBlobRef === undefined ? {} : { headBlobRef: branch.headBlobRef })
        })),
        latestRevision: input.latestRevision
    };
}
function markAncestorRevisions(input) {
    const visited = new Set();
    let currentRevision = input.startRevision;
    while (!visited.has(currentRevision)) {
        visited.add(currentRevision);
        const current = input.revisionMap.get(currentRevision);
        if (current === undefined) {
            input.onMissingParent(currentRevision);
            return;
        }
        if (current.parentRevision === null) {
            return;
        }
        const parent = input.revisionMap.get(current.parentRevision);
        if (parent === undefined) {
            input.onMissingParent(current.parentRevision);
            input.onUnknownParent(current.revision);
            return;
        }
        input.onAncestor(parent.revision);
        currentRevision = parent.revision;
    }
    input.onUnknownParent(currentRevision);
}
async function estimateFirebaseRevisionStorage(input) {
    if (!input.estimateStorage) {
        return zeroStorageEstimate();
    }
    if (input.revision.snapshotRef === undefined) {
        return null;
    }
    const blobSnapshot = await getDoc(input.refs.blob(input.repoId, input.revision.snapshotRef));
    if (!blobSnapshot.exists()) {
        return null;
    }
    const blobData = readDocumentData(blobSnapshot);
    return {
        bytes: jsonByteSize(input.revision) + jsonByteSize(blobData),
        documents: 2,
        blobs: 1
    };
}
function collectLiveBlobRefs(input) {
    const refs = new Set();
    for (const revision of input.revisions) {
        if (revision.snapshotRef !== undefined) {
            refs.add(revision.snapshotRef);
        }
    }
    for (const branch of input.branches) {
        if (branch.headBlobRef !== undefined) {
            refs.add(branch.headBlobRef);
        }
    }
    return refs;
}
async function listOrphanFirebaseBlobs(input) {
    const snapshots = await getDocs(input.refs.blobs(input.repoId));
    const orphans = [];
    for (const snapshot of snapshots.docs) {
        const data = readDocumentData(snapshot);
        const blobRef = blobRefFromSnapshot(snapshot.id, data);
        if (input.liveBlobRefs.has(blobRef)) {
            continue;
        }
        orphans.push({
            blobRef,
            reason: "orphan",
            estimatedStorage: input.estimateStorage
                ? {
                    bytes: jsonByteSize(data),
                    documents: 1,
                    blobs: 1
                }
                : zeroStorageEstimate()
        });
    }
    return orphans.sort((left, right) => left.blobRef.localeCompare(right.blobRef));
}
async function deleteGarbageCollectionBlobs(input) {
    const candidateBlobRefs = new Set([
        ...input.plan.orphanBlobs.map(blob => blob.blobRef),
        ...input.plan.deletableRevisions.flatMap(revision => revision.snapshotBlobRef === undefined ? [] : [revision.snapshotBlobRef])
    ]);
    const deleted = [];
    const skipped = [];
    for (const chunk of chunkItems(Array.from(candidateBlobRefs).sort(), 150)) {
        const revisions = await listAllFirebaseRevisions(input.refs, input.repoId);
        const branches = await listAllFirebaseBranches(input.refs, input.repoId);
        const liveBlobRefs = collectLiveBlobRefs({ revisions, branches });
        const chunkResult = await runTransaction(input.options.db, async (transaction) => {
            const chunkDeleted = [];
            const chunkSkipped = [];
            await readRepoOrThrowInTransaction(transaction, input.refs, input.repoId);
            for (const blobRef of chunk) {
                const blobSnapshot = await transaction.get(input.refs.blob(input.repoId, blobRef));
                if (!blobSnapshot.exists()) {
                    chunkSkipped.push({ blobRef, reason: "missing" });
                    continue;
                }
                if (liveBlobRefs.has(blobRef)) {
                    chunkSkipped.push({ blobRef, reason: "still-referenced" });
                    continue;
                }
                transaction.delete(input.refs.blob(input.repoId, blobRef));
                chunkDeleted.push(blobRef);
            }
            touchRepoDocument(transaction, input.refs, input.repoId, input.options.now());
            return {
                deleted: chunkDeleted,
                skipped: chunkSkipped
            };
        });
        deleted.push(...chunkResult.deleted);
        skipped.push(...chunkResult.skipped);
    }
    return { deleted, skipped };
}
async function estimateFirebaseStorage(input) {
    const includeRevisions = input.input.includeRevisions ?? true;
    const includeBlobs = input.input.includeBlobs ?? true;
    const includeHeads = input.input.includeHeads ?? true;
    const includeBranches = input.input.includeBranches ?? true;
    const includeTags = input.input.includeTags ?? true;
    const includeAdapterSpecific = input.input.adapterSpecific ?? true;
    const repoSnapshot = await getDoc(input.refs.repo(input.input.repoId));
    if (!repoSnapshot.exists()) {
        throw new RepositoryNotFoundError(`Repository "${input.input.repoId}" was not found.`);
    }
    let rawStateBytes = 0;
    let objectVcsMetadataBytes = jsonByteSize(readDocumentData(repoSnapshot));
    let blobBytes = 0;
    let documentCount = 1;
    let revisionCount = 0;
    let blobCount = 0;
    let branchCount = 0;
    let tagCount = 0;
    if (includeRevisions) {
        const snapshots = await getDocs(input.refs.revisions(input.input.repoId));
        revisionCount = snapshots.docs.length;
        documentCount += snapshots.docs.length;
        objectVcsMetadataBytes += snapshots.docs.reduce((total, snapshot) => total + jsonByteSize(readDocumentData(snapshot)), 0);
    }
    if (includeBlobs) {
        const snapshots = await getDocs(input.refs.blobs(input.input.repoId));
        blobCount = snapshots.docs.length;
        documentCount += snapshots.docs.length;
        for (const snapshot of snapshots.docs) {
            const data = readDocumentData(snapshot);
            const state = data.state;
            const stateBytes = jsonByteSize(state);
            rawStateBytes += stateBytes;
            blobBytes += jsonByteSize(data);
        }
    }
    if (includeHeads) {
        const snapshots = await getDocs(input.refs.heads(input.input.repoId));
        documentCount += snapshots.docs.length;
        objectVcsMetadataBytes += snapshots.docs.reduce((total, snapshot) => total + jsonByteSize(readDocumentData(snapshot)), 0);
    }
    if (includeBranches) {
        const snapshots = await getDocs(input.refs.branches(input.input.repoId));
        branchCount = snapshots.docs.length;
        documentCount += snapshots.docs.length;
        objectVcsMetadataBytes += snapshots.docs.reduce((total, snapshot) => total + jsonByteSize(readDocumentData(snapshot)), 0);
    }
    if (includeTags) {
        const snapshots = await getDocs(input.refs.tags(input.input.repoId));
        tagCount = snapshots.docs.length;
        documentCount += snapshots.docs.length;
        objectVcsMetadataBytes += snapshots.docs.reduce((total, snapshot) => total + jsonByteSize(readDocumentData(snapshot)), 0);
    }
    const adapterOverhead = includeAdapterSpecific
        ? documentCount * input.options.storageEstimate.fixedBytesPerDocument +
            revisionCount * input.options.storageEstimate.fixedBytesPerRevision +
            blobCount * input.options.storageEstimate.fixedBytesPerBlob
        : 0;
    return {
        repoId: input.input.repoId,
        rawStateBytes,
        objectVcsMetadataBytes,
        blobBytes,
        estimatedBackendBytes: objectVcsMetadataBytes + blobBytes + adapterOverhead,
        documentCount,
        revisionCount,
        blobCount,
        branchCount,
        tagCount,
        notes: [
            "Firestore storage estimate is approximate. It includes Object VCS JSON payloads and configurable per-document overhead, but it does not guarantee exact billed storage."
        ]
    };
}
function mapReasonRecords(reasons) {
    return Array.from(reasons.entries())
        .sort(([left], [right]) => left - right)
        .map(([revision, reasonSet]) => ({
        revision,
        reasons: Array.from(reasonSet).sort()
    }));
}
function sumStorageEstimates(estimates) {
    return estimates.reduce((total, estimate) => ({
        bytes: total.bytes + estimate.bytes,
        documents: total.documents + estimate.documents,
        blobs: total.blobs + estimate.blobs
    }), zeroStorageEstimate());
}
function zeroStorageEstimate() {
    return {
        bytes: 0,
        documents: 0,
        blobs: 0
    };
}
function chunkItems(items, size) {
    const chunks = [];
    for (let index = 0; index < items.length; index += size) {
        chunks.push(items.slice(index, index + size));
    }
    return chunks;
}
function blobRefFromSnapshot(snapshotId, data) {
    const document = data;
    return typeof document.blobRef === "string"
        ? document.blobRef
        : decodeId(snapshotId);
}
function checkExpectedHeadHash(head, expectedHeadHash) {
    if (expectedHeadHash !== undefined && expectedHeadHash !== head.stateHash) {
        throw new ConcurrencyConflictError(`Expected HEAD hash "${expectedHeadHash}", got "${head.stateHash}".`);
    }
}
function requireString(value, fieldName) {
    if (value === undefined) {
        throw new PersistenceError(`Missing Firestore field "${fieldName}".`);
    }
    return value;
}
function jsonByteSize(value) {
    return new TextEncoder().encode(stableStringify(value)).byteLength;
}
function isRecord(value) {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
        return false;
    }
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
//# sourceMappingURL=index.js.map